import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Cookie, X } from 'lucide-react';

// Lightweight cookie-consent banner wired to Google Consent Mode v2.
// - Consent DEFAULTS are set in public/index.html BEFORE GTM (EU/EEA/UK => denied).
// - This banner is shown only to EU-region visitors (detected via browser timezone,
//   no network/geo call) who haven't chosen yet, and calls gtag('consent','update', ...).
// - The choice is persisted and re-applied on every load so GTM always has the state.
const STORAGE_KEY = 'fd_consent_v1';

const consentPayload = (value) => ({
  ad_storage: value,
  ad_user_data: value,
  ad_personalization: value,
  analytics_storage: value,
});

const isEURegion = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    // Europe/* covers EU + EEA + UK + CH; Atlantic/* Canary/Faroe/Azores are EU territories.
    return tz.startsWith('Europe/') || ['Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Faroe', 'Atlantic/Reykjavik'].includes(tz);
  } catch (e) {
    return false;
  }
};

const applyConsent = (choice) => {
  const value = choice === 'accept' ? 'granted' : 'denied';
  if (window.gtag) window.gtag('consent', 'update', consentPayload(value));
  if (window.dataLayer) window.dataLayer.push({ event: 'consent_update', consent_choice: choice });
};

export const ConsentBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { stored = null; }
    if (stored && stored.choice) {
      applyConsent(stored.choice); // re-apply so GTM has state on every load
      return;
    }
    if (isEURegion()) setShow(true); // non-EU: defaults already granted, no banner needed
  }, []);

  const choose = (choice) => {
    applyConsent(choice);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() })); } catch (e) { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-4 sm:right-auto z-[300] sm:max-w-md rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-xl p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-9 w-9 rounded-full bg-accent/12 text-accent flex items-center justify-center">
          <Cookie className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">We value your privacy</p>
          <p className="mt-1 text-xs text-muted-foreground">
            We use cookies to analyse traffic and improve FlightDeals.in. You can accept all cookies or
            keep only those essential to run the site. See our{' '}
            <a href="/privacy" className="underline hover:text-foreground">privacy policy</a>.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => choose('accept')}
              className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 text-sm font-semibold px-4"
              data-testid="consent-accept-all"
            >
              Accept all
            </Button>
            <Button
              variant="outline"
              onClick={() => choose('reject')}
              className="h-9 rounded-xl text-sm px-4"
              data-testid="consent-reject-nonessential"
            >
              Only essential
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => choose('reject')}
          aria-label="Dismiss and keep only essential cookies"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="consent-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
